import {
  cloneItem,
  cloneSection,
  copyItemAudioToPage,
  copyItemImagesToPage,
  copyItemVideoToPage,
  copySectionAudioToPage,
  copySectionImagesToPage,
  copySectionVideoToPage,
  migratePageAssets,
  queueSectionImagesAsOrphaned,
} from '../services/PageAssetMigration';
import { queueOrphanedAudioForItem } from '../services/ItemAudioSync';
import { queueOrphanedVideoForItem } from '../services/ItemVideoSync';
import { queueOrphanedImagesForItem } from '../services/ItemImageSync';
import { syncItemAudioForFile } from '../services/ItemAudioSync';
import { syncItemVideoForFile } from '../services/ItemVideoSync';
import { syncItemImagesForFile } from '../services/ItemImageSync';
import { appendItemsToSectionBottom, insertItemIntoSection } from '../utils/itemMove';
import { displayNameFromPath, parentDirOfMarkdown, slugFromLabel } from '../utils/markdownPaths';
import { appAlert, appConfirm, appPrompt } from '../utils/appDialog';
import type { DatabaseFile, NavOrderEntry, NavOrderFile, Section } from '../types';
import {
  insertPathInNavOrder,
  patchNavOrderPathsForRename,
} from '../utils/navOrder';
import {
  makeNavOrderFile,
  patchNavOrderIcons,
  patchNavOrderIconsForPrefix,
} from '../utils/pageIcon';
import type { AppContext } from './appContext';
import { loadFiles, saveFile } from './fileLifecycle';
import {
  registerCrossPageAssetCopy,
  registerPendingNewPage,
  trackMoveTouched,
} from './pendingItemMoves';

export function mapPathUnderRenamedFolder(ctx: AppContext, p: string, oldPrefix: string, newPrefix: string): string {
    if (p === oldPrefix || p.startsWith(`${oldPrefix}/`)) {
      return newPrefix + p.slice(oldPrefix.length);
    }
    return p;
}

export function patchNavOrderForFolderRename(ctx: AppContext, oldPrefix: string, newPrefix: string): void {
    const map = (p: string) => mapPathUnderRenamedFolder(ctx, p, oldPrefix, newPrefix);
    const next: Record<string, NavOrderEntry[]> = {};
    for (const [key, list] of Object.entries(ctx.navOrderFile.order)) {
      next[map(key)] = list.map(e =>
        e.kind === 'file' ? { kind: 'file' as const, path: map(e.path) } : { kind: 'dir' as const, dir: map(e.dir) }
      );
    }
    ctx.navOrderFile = makeNavOrderFile(
      next,
      patchNavOrderIconsForPrefix(ctx.navOrderFile.icons, oldPrefix, newPrefix)
    );
}

export async function renameFolder(ctx: AppContext, oldDir: string, sidebarEl: HTMLElement, mainEl: HTMLElement): Promise<void> {
    const parts = oldDir.split('/').filter(Boolean);
    if (!oldDir.startsWith('database/') || parts.length < 2) return;

    if (ctx.fileView.hasUnsavedChanges()) {
      const cur = ctx.fileView.getCurrentFilePath();
      if (cur && (cur === oldDir || cur.startsWith(`${oldDir}/`))) {
        await appAlert('Save or discard changes to the open file before renaming this folder.');
        return;
      }
    }

    const oldSegment = parts[parts.length - 1]!;
    const parentDir = oldDir.slice(0, -(oldSegment.length + 1));

    const suggested = oldSegment.replace(/-/g, ' ');
    const label = await appPrompt('Enter the new folder name.', {
      title: 'Rename folder',
      inputLabel: 'Folder name',
      defaultValue: suggested,
      confirmLabel: 'Rename',
    });
    if (!label) return;

    const slug = slugFromLabel(label);
    if (!slug) {
      await appAlert('Use at least one letter or number in the name.');
      return;
    }
    if (slug === oldSegment) return;

    const newDir = `${parentDir}/${slug}`;

    const targetBusy = ctx.allPaths.some(p => p.startsWith(`${newDir}/`) || p === `${newDir}.md`);
    if (targetBusy) {
      await appAlert(`There are already files at ${newDir}/. Remove or rename them first.`);
      return;
    }

    const under = `${oldDir}/`;
    const toMove = ctx.allPaths.filter(p => p.startsWith(under)).sort((a, b) => a.localeCompare(b));
    if (toMove.length === 0) {
      await appAlert('This folder has no files under it.');
      return;
    }

    type Move = { oldPath: string; newPath: string; content: string; sha: string };
    const moves: Move[] = [];
    try {
      for (const oldPath of toMove) {
        const newPath = newDir + oldPath.slice(oldDir.length);
        const loaded = ctx.fileRegistry.files.find(f => f.path === oldPath);
        let content: string;
        let sha: string;
        if (loaded && ctx.fileRegistry.originalContents.has(oldPath)) {
          content = ctx.fileRegistry.originalContents.get(oldPath)!;
          sha = loaded.sha;
        } else {
          const r = await ctx.github.readFile(oldPath);
          content = r.content;
          sha = r.sha;
        }
        moves.push({ oldPath, newPath, content, sha });
      }

      for (const m of moves) {
        await ctx.github.renameFile(m.oldPath, m.newPath, m.content, m.sha);
        try {
          await migratePageAssets(ctx.github, m.oldPath, m.newPath);
        } catch (imgErr) {
          console.warn('Image folder migration after folder rename failed:', imgErr);
        }
      }
    } catch (err) {
      await appAlert(`Rename folder failed: ${(err as Error).message}\n\nReloading the file list from GitHub.`);
      await loadFiles(ctx);
      return;
    }

    for (const m of moves) {
      ctx.fileRegistry.originalContents.delete(m.oldPath);
      ctx.fileRegistry.originalContents.set(m.newPath, m.content);
    }

    ctx.allPaths = ctx.allPaths
      .map(p => mapPathUnderRenamedFolder(ctx, p, oldDir, newDir))
      .sort((a, b) => a.localeCompare(b));

    for (const m of moves) {
      const f = ctx.fileRegistry.files.find(x => x.path === m.oldPath);
      if (!f) continue;
      const { sha } = await ctx.github.readFile(m.newPath);
      const parsed = ctx.markdown.parse(m.newPath, m.content, sha);
      f.path = m.newPath;
      f.name = parsed.name;
      f.sha = sha;
    }

    const ap = ctx.sidebar.getActivePath();
    if (ap && (ap === oldDir || ap.startsWith(`${oldDir}/`))) {
      ctx.sidebar.setActivePath(mapPathUnderRenamedFolder(ctx, ap, oldDir, newDir));
    }

    patchNavOrderForFolderRename(ctx, oldDir, newDir);
    try {
      await ctx.navOrder.persist();
    } catch {
      /* ignore */
    }

    ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);

    const active = ctx.sidebar.getActivePath();
    const open = ctx.fileRegistry.files.find(f => f.path === active);
    if (open) {
      ctx.fileView.render(mainEl, open);
    }
}

export async function setPageIcon(ctx: AppContext, path: string, icon: string, sidebarEl: HTMLElement, mainEl: HTMLElement): Promise<void> {
    const icons = { ...(ctx.navOrderFile.icons ?? {}) };
    if (!icon) delete icons[path];
    else icons[path] = icon;

    ctx.navOrderFile = makeNavOrderFile(ctx.navOrderFile.order, icons);
    try {
      await ctx.navOrder.persist();
      ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
      const open = ctx.fileRegistry.files.find(f => f.path === path);
      if (open && ctx.fileView.getCurrentFilePath() === path) {
        ctx.fileView.render(mainEl, open);
      }
    } catch (err) {
      await appAlert(`Could not save page icon: ${(err as Error).message}`);
    }
}

export async function applyNavOrderFromSidebar(ctx: AppContext, next: NavOrderFile, sidebarEl: HTMLElement, mainEl: HTMLElement, fileRelocations: ReadonlyArray<{ oldPath: string; newPath: string }>): Promise<void> {
    const relocs = fileRelocations.filter(r => r.oldPath !== r.newPath);
    const uniqueOrigins = new Set(relocs.map(r => r.oldPath));
    if (uniqueOrigins.size > 1) {
      await appAlert('Something went wrong while moving files in the sidebar. Reloading from GitHub.');
      await loadFiles(ctx);
      return;
    }

    if (relocs.length === 1) {
      const { oldPath, newPath } = relocs[0]!;
      if (ctx.fileView.hasUnsavedChanges()) {
        const cur = ctx.fileView.getCurrentFilePath();
        if (cur === oldPath) {
          await appAlert('Save or discard changes before moving this file to another folder.');
          ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
          return;
        }
      }
      if (ctx.allPaths.includes(newPath)) {
        await appAlert(`Cannot move: a file already exists at ${newPath}.`);
        ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
        return;
      }

      const fileObj = ctx.fileRegistry.files.find(f => f.path === oldPath);
      let content: string;
      let sha: string;
      if (fileObj && ctx.fileRegistry.originalContents.has(oldPath)) {
        content = ctx.fileRegistry.originalContents.get(oldPath)!;
        sha = fileObj.sha;
      } else {
        const r = await ctx.github.readFile(oldPath);
        content = r.content;
        sha = r.sha;
      }

      try {
        await ctx.github.renameFile(oldPath, newPath, content, sha);
        try {
          await migratePageAssets(ctx.github, oldPath, newPath);
        } catch (imgErr) {
          console.warn('Image folder migration after sidebar move failed:', imgErr);
        }
      } catch (err) {
        await appAlert(`Could not move file: ${(err as Error).message}`);
        ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
        return;
      }

      ctx.fileRegistry.originalContents.delete(oldPath);
      ctx.fileRegistry.originalContents.set(newPath, content);

      const ix = ctx.allPaths.indexOf(oldPath);
      if (ix >= 0) ctx.allPaths[ix] = newPath;
      ctx.allPaths.sort((a, b) => a.localeCompare(b));

      if (fileObj) {
        fileObj.path = newPath;
        const parsed = ctx.markdown.parse(newPath, content, '');
        fileObj.name = parsed.name;
        const { sha: freshSha } = await ctx.github.readFile(newPath);
        fileObj.sha = freshSha;
      }

      if (ctx.sidebar.getActivePath() === oldPath) {
        ctx.sidebar.setActivePath(newPath);
      }
    }

    if (relocs.length === 1) {
      const { oldPath, newPath } = relocs[0]!;
      const patched = patchNavOrderIcons(next.icons, oldPath, newPath);
      if (patched) next.icons = patched;
      else delete next.icons;
    }

    const orderUnchanged =
      relocs.length === 0 && JSON.stringify(ctx.navOrderFile.order) === JSON.stringify(next.order);
    if (orderUnchanged) return;

    ctx.navOrderFile = next;
    try {
      await ctx.navOrder.persist();
      ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
      const active = ctx.sidebar.getActivePath();
      const open = ctx.fileRegistry.files.find(f => f.path === active);
      if (open) {
        ctx.fileView.render(mainEl, open);
      }
    } catch (err) {
      await appAlert(`Could not save sidebar order: ${(err as Error).message}`);
      if (relocs.length > 0) {
        await loadFiles(ctx);
      } else {
        ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
      }
    }
}

export async function pathsBlockCrossFileOps(ctx: AppContext, paths: string[]): Promise<boolean> {
    if (!ctx.fileView.hasUnsavedChanges()) return false;
    const cur = ctx.fileView.getCurrentFilePath();
    if (cur && paths.includes(cur)) {
      await appAlert('Save or discard changes before moving sections or pages.');
      return true;
    }
    return false;
}

export async function moveSectionToNewPage(ctx: AppContext, source: DatabaseFile, sectionIndex: number, pageName: string, sidebarEl: HTMLElement, mainEl: HTMLElement): Promise<void> {
    const parentDir = parentDirOfMarkdown(source.path);
    const slug = slugFromLabel(pageName);
    if (!slug) {
      await appAlert('Use at least one letter or number in the page name.');
      return;
    }
    const newPath = `${parentDir}/${slug}.md`;
    if (ctx.allPaths.includes(newPath)) {
      await appAlert(`A file already exists at ${newPath}.`);
      return;
    }
    if (await pathsBlockCrossFileOps(ctx, [source.path])) return;

    const section = cloneSection(source.sections[sectionIndex]!);
    queueSectionImagesAsOrphaned(source, source.sections[sectionIndex]!);
    source.sections.splice(sectionIndex, 1);

    const originalNew = `# ${pageName.trim()}\n\n`;
    const newFile = ctx.markdown.parse(newPath, originalNew, '');
    newFile.sections.push(section);

    try {
      await copySectionImagesToPage(ctx.github, source.path, newPath, section);
      await copySectionAudioToPage(ctx.github, source.path, newPath, section);
      await copySectionVideoToPage(ctx.github, source.path, newPath, section);
      await saveFile(ctx, source);
      ctx.fileRegistry.originalContents.set(newPath, originalNew);
      await syncItemImagesForFile(ctx.github, newFile);
      await syncItemAudioForFile(ctx.github, newFile);
      await syncItemVideoForFile(ctx.github, newFile);
      const body = ctx.markdown.serialize(newFile, originalNew);
      await ctx.github.writeFile(newPath, body, '', `Move section to new page: ${newPath.split('/').pop()}`);
      const { content, sha } = await ctx.github.readFile(newPath);
      ctx.fileRegistry.originalContents.set(newPath, content);
      newFile.sha = sha;

      ctx.allPaths.push(newPath);
      ctx.allPaths.sort((a, b) => a.localeCompare(b));
      ctx.fileRegistry.files.push(newFile);
      newFile.name = displayNameFromPath(newPath);
      ctx.navOrderFile = insertPathInNavOrder(ctx.navOrderFile, newPath);
      try {
        await ctx.navOrder.persist();
      } catch {
        /* ignore */
      }

      ctx.revokeImageObjectUrls();
      ctx.sidebar.setActivePath(newPath);
      ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
      ctx.fileView.render(mainEl, newFile);
    } catch (err) {
      await appAlert(`Could not move section: ${(err as Error).message}`);
      await loadFiles(ctx);
    }
}

export async function moveSectionToExistingPage(ctx: AppContext, source: DatabaseFile, sectionIndex: number, targetPath: string, sidebarEl: HTMLElement, mainEl: HTMLElement): Promise<void> {
    if (targetPath === source.path) return;
    if (!ctx.allPaths.includes(targetPath)) {
      await appAlert('That page no longer exists. Reloading the file list.');
      await loadFiles(ctx);
      return;
    }
    if (await pathsBlockCrossFileOps(ctx, [source.path, targetPath])) return;

    const section = cloneSection(source.sections[sectionIndex]!);
    queueSectionImagesAsOrphaned(source, source.sections[sectionIndex]!);
    source.sections.splice(sectionIndex, 1);

    try {
      const target = await ctx.fileRegistry.ensureLoaded(targetPath);
      target.sections.push(section);
      await copySectionImagesToPage(ctx.github, source.path, targetPath, section);
      await copySectionAudioToPage(ctx.github, source.path, targetPath, section);
      await copySectionVideoToPage(ctx.github, source.path, targetPath, section);
      await saveFile(ctx, source);
      await saveFile(ctx, target);

      ctx.revokeImageObjectUrls();
      ctx.sidebar.setActivePath(targetPath);
      ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
      ctx.fileView.render(mainEl, target);
    } catch (err) {
      await appAlert(`Could not move section: ${(err as Error).message}`);
      await loadFiles(ctx);
    }
}

export async function moveFileToFolder(ctx: AppContext, file: DatabaseFile, targetParentDir: string, sidebarEl: HTMLElement, mainEl: HTMLElement): Promise<void> {
    const base = file.path.split('/').pop()!;
    const newPath = `${targetParentDir}/${base}`;
    if (newPath === file.path) return;
    if (ctx.allPaths.includes(newPath)) {
      await appAlert(`Cannot move: a file already exists at ${newPath}.`);
      return;
    }
    if (await pathsBlockCrossFileOps(ctx, [file.path])) return;

    const oldPath = file.path;
    const original = ctx.fileRegistry.originalContents.get(oldPath);
    const content = original ?? ctx.markdown.serialize(file, `# ${file.name}\n\n`);
    const sha = file.sha;

    try {
      await ctx.github.renameFile(oldPath, newPath, content, sha);
      await migratePageAssets(ctx.github, oldPath, newPath);
    } catch (err) {
      await appAlert(`Could not move file: ${(err as Error).message}`);
      return;
    }

    ctx.fileRegistry.originalContents.delete(oldPath);
    ctx.fileRegistry.originalContents.set(newPath, content);
    file.path = newPath;

    const ix = ctx.allPaths.indexOf(oldPath);
    if (ix >= 0) ctx.allPaths[ix] = newPath;
    ctx.allPaths.sort((a, b) => a.localeCompare(b));

    ctx.navOrderFile = patchNavOrderPathsForRename(ctx.navOrderFile, oldPath, newPath);
    try {
      await ctx.navOrder.persist();
    } catch {
      /* ignore */
    }

    ctx.revokeImageObjectUrls();
    ctx.sidebar.setActivePath(newPath);
    ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
    ctx.fileView.render(mainEl, file);
}

export async function moveItemToNewPage(
  ctx: AppContext,
  source: DatabaseFile,
  sectionIndex: number,
  itemIndex: number,
  pageName: string,
  _sidebarEl: HTMLElement,
  _mainEl: HTMLElement
): Promise<void> {
  const parentDir = parentDirOfMarkdown(source.path);
  const slug = slugFromLabel(pageName);
  if (!slug) {
    await appAlert('Use at least one letter or number in the page name.');
    return;
  }
  const newPath = `${parentDir}/${slug}.md`;
  if (ctx.allPaths.includes(newPath)) {
    await appAlert(`A file already exists at ${newPath}.`);
    return;
  }

  const section = source.sections[sectionIndex];
  const existing = section?.items[itemIndex];
  if (!section || !existing) return;

  const item = cloneItem(existing);
  queueOrphanedImagesForItem(source, existing);
  queueOrphanedAudioForItem(source, existing);
  queueOrphanedVideoForItem(source, existing);
  section.items.splice(itemIndex, 1);

  const starterContent = `# ${pageName.trim()}\n\n## Items\n\n`;
  registerPendingNewPage(ctx, {
    sourcePath: source.path,
    newPath,
    pageName: pageName.trim(),
    starterContent,
    item,
  });
  trackMoveTouched(ctx, source.path);

  ctx.revokeImageObjectUrls();
  ctx.fileView.markDirtyAndRefresh();
}

export async function moveAllItemsToSection(
  ctx: AppContext,
  source: DatabaseFile,
  fromSectionIndex: number,
  targetPath: string,
  targetSectionIndex: number,
  _sidebarEl: HTMLElement,
  _mainEl: HTMLElement
): Promise<void> {
  if (fromSectionIndex === targetSectionIndex && targetPath === source.path) {
    await appAlert('Choose a different section.');
    return;
  }

  const fromSection = source.sections[fromSectionIndex];
  if (!fromSection || fromSection.items.length === 0) {
    await appAlert('This section has no items to move.');
    return;
  }

  if (!ctx.allPaths.includes(targetPath)) {
    await appAlert('That page no longer exists. Reloading the file list.');
    await loadFiles(ctx);
    return;
  }

  const samePage = targetPath === source.path;
  const moved = fromSection.items.map(cloneItem);
  if (!samePage) {
    for (const existing of fromSection.items) {
      queueOrphanedImagesForItem(source, existing);
      queueOrphanedAudioForItem(source, existing);
      queueOrphanedVideoForItem(source, existing);
    }
  }
  fromSection.items.length = 0;
  const target = samePage ? source : await ctx.fileRegistry.ensureLoaded(targetPath);
  const toSection = target.sections[targetSectionIndex];
  if (!toSection) {
    await appAlert('That section no longer exists. Reloading the file list.');
    await loadFiles(ctx);
    return;
  }

  appendItemsToSectionBottom(toSection, moved);
  if (!samePage) {
    registerCrossPageAssetCopy(ctx, source.path, targetPath, moved);
  }
  trackMoveTouched(ctx, source.path);

  ctx.revokeImageObjectUrls();
  ctx.fileView.markDirtyAndRefresh();
}

export async function moveItemToSection(
  ctx: AppContext,
  source: DatabaseFile,
  fromSectionIndex: number,
  itemIndex: number,
  targetPath: string,
  targetSectionIndex: number,
  _sidebarEl: HTMLElement,
  _mainEl: HTMLElement
): Promise<void> {
  if (!ctx.allPaths.includes(targetPath)) {
    await appAlert('That page no longer exists. Reloading the file list.');
    await loadFiles(ctx);
    return;
  }
  if (fromSectionIndex === targetSectionIndex && targetPath === source.path) {
    await appAlert('Choose a different section.');
    return;
  }

  const fromSection = source.sections[fromSectionIndex];
  const existing = fromSection?.items[itemIndex];
  if (!fromSection || !existing) return;

  const item = cloneItem(existing);
  const samePage = targetPath === source.path;
  if (!samePage) {
    queueOrphanedImagesForItem(source, existing);
    queueOrphanedAudioForItem(source, existing);
    queueOrphanedVideoForItem(source, existing);
  }
  fromSection.items.splice(itemIndex, 1);

  const target = samePage ? source : await ctx.fileRegistry.ensureLoaded(targetPath);
  const toSection = target.sections[targetSectionIndex];
  if (!toSection) {
    await appAlert('That section no longer exists. Reloading the file list.');
    await loadFiles(ctx);
    return;
  }

  insertItemIntoSection(toSection, item);
  if (!samePage) {
    registerCrossPageAssetCopy(ctx, source.path, targetPath, [item]);
  }
  trackMoveTouched(ctx, source.path);

  ctx.revokeImageObjectUrls();
  ctx.fileView.markDirtyAndRefresh();
}